import { Space } from 'antd';
import React, { useEffect, useState } from 'react';
import DeleteModal from '../../components/DeleteModal';
import DropdownInput from '../../components/DropdownInput';
import ImageUpload from '../../components/ImageUpload';
import Input from '../../components/Input';
import LanguageTab from '../../components/LanguageTab';
import ModalWrapper from '../../components/ModalWrapper';
import RadioGroup from '../../components/RadioGroup';
import SwitchComponent from '../../components/SwitchComponent';
import HTMLEditor from '../../components/HTMLEditor/HTMLEditor';
import { useContentCreate } from '../../hooks/useContentCreate';
import { useContentDelete } from '../../hooks/useContentDelete';
import { useContentDetail } from '../../hooks/useContentDetail';
import useMultilanguageFormValidation from '../../hooks/useMultilanguageFormValidation';
import { useMultilanguageState, type MultilanguageState } from '../../hooks/useMultilanguageState';
import { useUpdateContent } from '../../hooks/useUpdateContent';
import type { ContentImage, CreateContentRequest, UpdateContentRequest } from '../../schemas/auth';
import styles from '../../style/components/common.module.scss';

interface BlogModalProps {
  blogId: 'create' | string;
  onClose: () => void;
  onCancel?: () => void;
  currentItems?: { id: string; rank: number }[];
}

const BlogModal: React.FC<BlogModalProps> = ({ blogId, onClose, onCancel, currentItems }) => {
  const { data: blogData, isLoading } = useContentDetail(blogId);
  const createContentMutation = useContentCreate({
    onSuccess: onClose,
    successMessage: 'Blog Created',
    successDescription: 'The blog has been created successfully.',
  });
  const deleteContentMutation = useContentDelete({
    onSuccess: () => {
      setShowDeleteModal(false);
      onClose();
    },
  });
  const updateContentMutation = useUpdateContent({
    onSuccess: () => {
      onClose();
    },
  });

  const isUpdate = blogId !== 'create';
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [blogName, setBlogName] = useState('');
  const [statusEnabled, setStatusEnabled] = useState<boolean>(true);
  const [enableJoinButton, setEnableJoinButton] = useState<'Yes' | 'No'>('Yes');
  const [redirectPage, setRedirectPage] = useState<string>();
  const [rank, setRank] = useState<number>(0);

  const [attempted, setAttempted] = useState(false);
  const [language, setLanguage] = useState<string>('tr');

  const [blogTitle, setBlogTitle] = useMultilanguageState<string>('');
  const [shortDescription, setShortDescription] = useMultilanguageState<string>('');
  const [blogText, setBlogText] = useMultilanguageState<string>('');
  const [blogImage, setBlogImage] = useMultilanguageState<ContentImage>();

  const validations = useMultilanguageFormValidation({
    blogTitle,
    shortDescription,
    blogText,
    blogImage,
  });

  useEffect(() => {
    if (blogData) {
      setBlogName(blogData.name || '');
      setStatusEnabled(blogData.status === 'Active');
      setRank(blogData.rank || 0);

      setBlogTitle(blogData.title || {});
      setShortDescription(blogData.shortDescription || {});
      setBlogText(blogData.description || {});

      const images: MultilanguageState<ContentImage> = {};

      if (blogData.imageUrl && blogData.imageUrl.length > 0) {
        blogData.imageUrl.forEach((image) => {
          const document = image.documents[0];
          images[image.languageId] = {
            id: document.id,
            environmentType: 'Mobile',
            languageId: image.languageId,
            document: document.publicUrl,
          };
        });
        setBlogImage(images);
      }

      if (!blogData.redirectUrl || blogData.redirectUrl === '' || blogData.redirectUrl === null) {
        setEnableJoinButton('No');
        setRedirectPage(undefined);
      } else if (blogData.redirectUrl.startsWith('inapplink://')) {
        setEnableJoinButton('Yes');
        setRedirectPage(blogData.redirectUrl.replace('inapplink://', ''));
      } else {
        setEnableJoinButton('Yes');
        setRedirectPage(blogData.redirectUrl);
      }
    }
  }, [blogData]);

  const handleSave = async () => {
    if (!blogName || (enableJoinButton === 'Yes' && !redirectPage) || !validations?.form) {
      setAttempted(true);
      return;
    }

    setAttempted(false);

    const images: ContentImage[] = [];
    for (const languageKey in blogImage) {
      const image = blogImage[languageKey];
      if (image.document instanceof File) {
        images.push({
          languageId: languageKey,
          document: image.document,
          environmentType: 'Mobile',
          id: image.id,
        });
      }
    }

    let redirectUrl = '';
    if (enableJoinButton === 'Yes' && redirectPage) {
      redirectUrl = `inapplink://${redirectPage}`;
    }
    const payload: UpdateContentRequest | CreateContentRequest = {
      name: blogName.trim(),
      title: blogTitle,
      description: blogText,
      shortDescription: shortDescription,
      image: images,
      redirectUrl,
      type: 'Blog',
      rank,
      status: statusEnabled ? 'Active' : 'Passive',
    };

    if (isUpdate) {
      payload.rank = rank;
      updateContentMutation.mutate({
        id: blogId,
        payload: payload as UpdateContentRequest,
      });
    } else {
      await createContentMutation.mutateAsync(payload);
    }
  };

  return (
    <ModalWrapper
      open={true}
      onClose={onClose}
      onSave={handleSave}
      onCancel={onCancel}
      onDelete={() => setShowDeleteModal(true)}
      deleteButton={isUpdate}
      title={isUpdate ? 'Update Blog' : 'Create Blog'}
      width={450}
      saveButtonText={isUpdate ? 'Update' : 'Create'}
      cancelButtonText="Cancel"
      saveLoading={isUpdate ? updateContentMutation.isPending : createContentMutation.isPending}
      loading={isLoading}
    >
      <div className={styles.modalContentTop}>
        <Space
          direction="vertical"
          size="large"
          style={{ width: '100%' }}
        >
          <Input
            label="Blog Title"
            placeholder="Blog Title"
            value={blogName}
            currentLanguage={language}
            onChange={setBlogName}
            maxLength={40}
            showCount={true}
            required
            error={attempted && !blogName.trim()}
            errorMessage="Blog name is required"
          />

          <RadioGroup
            label='Enable "Discover" Button?'
            options={[
              { value: 'Yes', label: 'Yes' },
              { value: 'No', label: 'No' },
            ]}
            value={enableJoinButton}
            onChange={setEnableJoinButton}
            direction="horizontal"
            required
          />

          {enableJoinButton === 'Yes' && (
            <DropdownInput
              required
              label="Page to be redirected"
              placeholder="Select"
              value={redirectPage}
              onChange={(value) => setRedirectPage(value)}
              options={[
                { value: 'qr', label: 'QR' },
                { value: 'profile', label: 'Profile' },
                { value: 'faq', label: 'FAQ' },
                { value: 'personalInfo', label: 'Personal Info' },
                { value: 'history', label: 'History' },
                { value: 'home', label: 'Home' },
                { value: 'login', label: 'Login' },
                { value: 'register', label: 'Register' },
                { value: 'permissions', label: 'Permissions' },
                { value: 'legals', label: 'Legals' },
                { value: 'branches', label: 'Branches' },
              ]}
              error={attempted && enableJoinButton === 'Yes' && !redirectPage}
              errorMessage="Please select a page"
            />
          )}

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <label
              style={{
                fontSize: '14px',
                fontWeight: 500,
                color: 'var(--color-text-secondary)',
                margin: 0,
              }}
            >
              Status
            </label>
            <SwitchComponent
              theme="primary"
              checked={statusEnabled}
              onChange={setStatusEnabled}
            />
          </div>
        </Space>
      </div>

      <Space
        direction="vertical"
        size="large"
        style={{ width: '100%' }}
      >
        <LanguageTab
          value={language}
          onChange={setLanguage}
          validations={validations?.general}
          attempted={attempted}
        />

        <Input
          label="Title"
          placeholder="Title"
          value={blogTitle}
          currentLanguage={language}
          onChange={setBlogTitle}
          required
          error={attempted && validations?.blogTitle}
          errorMessage="Title is required"
        />

        <Input
          label="Short description"
          placeholder="Short description"
          value={shortDescription}
          currentLanguage={language}
          onChange={setShortDescription}
          required
          error={attempted && validations?.shortDescription}
          errorMessage="Short description is required"
        />

        <HTMLEditor
          label="Blog text"
          value={blogText}
          onChange={setBlogText}
          currentLanguage={language}
          placeholder="Enter blog text..."
          required
          error={attempted && validations?.blogText}
          errorMessage="Blog text is required"
        />

        <ImageUpload
          label="Blog image"
          placeholder="Click to upload or drag and drop"
          value={blogImage}
          onChange={setBlogImage}
          currentLanguage={language}
          attempted={attempted}
          acceptedFormats={['image/png', 'image/jpeg']}
          maxSize={5}
          maxWidth={1029}
          maxHeight={552}
          required
          error={attempted && validations?.blogImage}
          errorMessage="Blog image is required"
        />
      </Space>

      {showDeleteModal && (
        <DeleteModal
          onClose={() => {
            setShowDeleteModal(false);
          }}
          onConfirm={() =>
            deleteContentMutation.mutate({
              id: blogId,
              deletedRank: blogData?.rank,
              currentItems,
            })
          }
          title="Delete blog?"
          message="Are you sure you want to delete this blog? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          loading={deleteContentMutation.isPending}
          itemName="blog"
        />
      )}
    </ModalWrapper>
  );
};

export default BlogModal;
